import {
  RouteAdmission,
  StoreAccessBoundary,
  SessionUnavailable,
  useDirtyAdmissionHold,
} from "@/shared/access/StoreAccess";
import { canAccess, routeAccessRule } from "@/shared/access/store-access";
import { AppShell } from "@/shared/layout/AppShell";
import { NotificationsProvider } from "@/shared/notifications/NotificationsProvider";
import { FeatureFlagsProvider } from "@/shared/feature-flags/FeatureFlagsProvider";
import { Navigate, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { LoadingSkeleton } from "@a2z/ui";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useSessionQuery } from "@/shared/services/useSessionQuery";
import { RouteErrorBoundary } from "@/shared/error/RouteErrorBoundary";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { useLocale } from "./locale";
import { RestaurantStoreTerminal } from "@/shared/layout/RestaurantStoreTerminal";
import { HelpLauncher } from "@/shared/help/components/HelpLauncher";
import { HelpDemoProvider } from "@/shared/help/HelpDemoContext";

/**
 * Authenticated layout for the storefront editor. Does the same session +
 * store-scope checks as `ProtectedLayout`, but skips `AppShell` (sidebar +
 * topbar chrome) so the editor can occupy the full viewport. The editor's
 * own `EditorShell` component owns its layout (top bar, three-pane grid).
 *
 * Onboarding redirect is intentionally NOT applied here — sellers may want
 * to preview the editor before completing onboarding.
 */
export function ProtectedEditorLayout() {
  const locale = useLocale();
  const { storeSlug = "" } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const t = useTranslations();
  const { data, isLoading, error, refetch } = useSessionQuery({ storeSlug });
  // Page help runs in the EDITOR too — the same registry, the same launcher
  // (that is the point of a registry). Declared ABOVE the early returns below,
  // which bail out for login, store mismatch, loading and restaurant stores: a
  // hook after any of those changes the hook count between renders.
  const [helpOpen, setHelpOpen] = useState(false);
  const [helpAnchor, setHelpAnchor] = useState<string | null>(null);
  // Whether the plan (and role) can open the editor. Read above the early
  // returns for the same hook-count reason; "admitted" until the session says
  // otherwise.
  const relative = location.pathname.slice(`/${locale}/${storeSlug}/`.length);
  const editorAdmitted = !(
    data?.seller.storeAccess === true &&
    data.seller.access &&
    !canAccess(data.seller, routeAccessRule(relative + location.search))
  );
  // A downgrade while the seller is IN the editor with edits not yet saved
  // asks first (same dialog as every page inside the shell) instead of
  // swapping the editor for the locked screen. The editor registers its
  // unsaved state with lib/navigation/dirty-forms.ts.
  const hold = useDirtyAdmissionHold(location.pathname + location.search, editorAdmitted);

  useEffect(() => {
    if (
      error instanceof ApiJsonClientError &&
      (error.status === 401 || error.status === 403)
    ) {
      navigate(`/${locale}/login`, { replace: true });
      return;
    }

    const authenticatedStoreSlug = data?.seller?.storeSlug?.trim();
    if (authenticatedStoreSlug && authenticatedStoreSlug !== storeSlug) {
      navigate(`/${locale}/${authenticatedStoreSlug}/store/editor`, { replace: true });
    }
  }, [data?.seller?.storeSlug, error, locale, navigate, storeSlug]);

  if (
    error instanceof ApiJsonClientError &&
    (error.status === 401 || error.status === 403)
  ) {
    return <Navigate to={`/${locale}/login`} replace />;
  }

  const authenticatedStoreSlug = data?.seller?.storeSlug?.trim();
  if (authenticatedStoreSlug && authenticatedStoreSlug !== storeSlug) {
    return (
      <Navigate
        to={`/${locale}/${authenticatedStoreSlug}/store/editor`}
        replace
      />
    );
  }

  if (isLoading) {
    return (
      <div className="flex min-h-[100svh] w-full items-center justify-center bg-[color:var(--background)] p-6">
        <div role="status" aria-busy="true" className="w-full max-w-xl">
          <span className="sr-only">{t(keys.common.loading)}</span>
          <LoadingSkeleton variant="card" rows={3} />
        </div>
      </div>
    );
  }

  if (error || !data) return <SessionUnavailable locale={locale} retry={refetch} />;

  if (data?.mustChangePassword) {
    return <Navigate to={`/${locale}/change-password`} replace />;
  }

  // Decision D13 — a RESTAURANT store is administered only in apps/restaurant.
  // The check lives HERE, at the protected layout, rather than in the nav: a
  // hidden nav still leaves every seller route reachable by URL, so a deep link
  // to /{locale}/{slug}/orders would render the page and the portal would be
  // half-usable — exactly what the decision exists to prevent.
  if (data?.seller?.storeKind === "RESTAURANT") {
    return (
      <RestaurantStoreTerminal
        locale={locale}
        storeSlug={storeSlug}
        availableStores={data.availableStores ?? []}
      />
    );
  }

  // A store whose plan (or a member whose role) cannot open the editor gets
  // the locked card INSIDE the normal shell — sidebar, top bar, bottom nav —
  // like every other locked page, not a bare full-viewport card with no way
  // around the portal. The editor's own full-screen layout is only for a
  // seller who can use it.
  if (!editorAdmitted && !hold.showPage) {
    return (
      <StoreAccessBoundary session={data} locale={locale} storeSlug={storeSlug} routeAdmission="shell">
        <FeatureFlagsProvider flags={data.featureFlags}>
          <NotificationsProvider sellerId={data.seller.id} storeId={data.seller.storeId}>
            <AppShell locale={locale} storeSlug={storeSlug} loginUrl={`/${locale}/login`}>
              <RouteAdmission>{null}</RouteAdmission>
            </AppShell>
          </NotificationsProvider>
        </FeatureFlagsProvider>
      </StoreAccessBoundary>
    );
  }

  return (
    // `100dvh` — the DYNAMIC viewport. The editor's mobile sheet is
    // `position: fixed`, so it is always laid out against the VISIBLE viewport;
    // this grid must use the unit that tracks the same thing, or the two drift
    // apart by the URL-bar height and the bottom publish-bar row lands under
    // the sheet.
    //   · `100vh`  == the LARGE viewport (bar retracted, 854px on a Galaxy S23
    //                 FE) — too tall while the bar is shown.
    //   · `100svh` == the SMALL viewport (bar shown, 782px) — a STATIC height:
    //                 correct while the bar is shown, but it does not grow when
    //                 the bar retracts, so the fixed sheet slides 72px down and
    //                 covers the publish bar.
    //   · `100dvh` == whatever is visible right now. Always agrees with `fixed`.
    // Belt-and-braces: `body { min-height: 100dvh }` (styles.css) removes the
    // phantom 72px of scroll that let the bar retract in the first place, so in
    // practice dvh stays pinned at the small viewport here and never animates.
    // Desktop is unaffected (dvh == vh with no retractable browser UI). None of
    // this reproduces in Playwright/devtools, where there is no dynamic URL bar
    // and vh == svh == dvh == the visible height.
    // While the unsaved-edits question is open (or the seller chose "Stay")
    // the boundary must not swap the editor out itself: admission is held here.
    <StoreAccessBoundary
      session={data}
      locale={locale}
      storeSlug={storeSlug}
      routeAdmission={editorAdmitted ? "here" : "shell"}
    >
    <div key={location.pathname} className="h-[100dvh] w-screen overflow-hidden">
      <RouteErrorBoundary>
        <HelpDemoProvider active={helpOpen} anchor={helpAnchor}>
          <Outlet />
        </HelpDemoProvider>
      </RouteErrorBoundary>
      {/* No setup checklist here — the editor is not part of it — so the
          floating corner belongs to page help outright. */}
      <HelpLauncher
        storeSlug={storeSlug}
        open={helpOpen}
        onOpenChange={setHelpOpen}
        onAnchorChange={setHelpAnchor}
      />
      {hold.dialog}
    </div>
    </StoreAccessBoundary>
  );
}
